import React, { useState, useEffect, useContext } from 'react';

import {
  Modal,
  ModalBody,
  ModalOverlay,
  ModalContent,
  rem,
  Grid,
  Flex,
  useTheme,
  Spinner,
  Link,
  useColorMode,
  Box,
} from '@vimeo/bokeh';
import { ArrowRightSmall } from '@vimeo/bokeh-icons';
import { useIsMobile } from '@vimeo/core/hooks/use-is-mobile';

import BpTracking from '../../utils/bpTracking';
import {
  ADVANCED_ENTERPRISE_UPSELL_FORM_URL,
  CLOSE_EVENT_DATA,
  defaultBpEventParams,
} from '../../utils/constants';
import { enterpriseTemplateProps, UserData, TrackingProps } from '../../utils/interface';
import translations from '../../utils/translations';
import { isSeatTierPlan, getDeviceType, getEnterpriseFooterLinkText } from '../../utils/utils';
import DismissIcon from '../common/DismissIcon';
import TrackingContext from '../common/TrackingContext';
import FeatureItems from './FeatureItems';
import Iframe from './Iframe';
import Title from './Title';

interface EnterpriseModalProps {
  userData: UserData | null;
  onClose: VoidFunction;
  tracking: TrackingProps;
  modalConfig?: enterpriseTemplateProps;
}

const EnterpriseModal = ({ userData, onClose, tracking, modalConfig }: EnterpriseModalProps) => {
  const [isLoadingIframe, setIsLoadingIframe] = useState(true);
  const { colors } = useTheme();
  const { colorMode } = useColorMode();
  const isMobile = useIsMobile();

  const trackingProps = useContext(TrackingContext);

  const iFrameLoadHandler = () => {
    setIsLoadingIframe(false);

    const iframe = document.getElementById('marktoFromIframe') as HTMLIFrameElement | null;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;

    const tryFindForm = () => {
      const formContainer = iframeDoc?.querySelector(
        'div#__next > div:nth-child(3) > div > div',
      ) as HTMLDivElement | null;

      // If form container is not found, try again till it finds somethings
      if (!formContainer) {
        setTimeout(tryFindForm, 100);
        return;
      }

      const style = iframeDoc?.createElement('style');

      // Manipulate the style from the form that is already loaded
      if (style) {
        style.textContent = `

        div#__next > div:nth-child(3) > div > div > div > form > div.mktoButtonRow > span > button:hover {
          color: ${colors.blackAlpha[900]} !important;
        }

        @media screen and (max-width: ${rem(375)}) {
          div#__next > div:nth-child(3) > div > div {
            overflow: auto;
            padding: ${rem(24)} ${rem(24)} 0 ${rem(24)};
            border-bottom: ${rem(24)} solid ${
          colorMode === 'dark' ? colors.gray[800] : colors.white
        };
          }

          div#__next > div:nth-child(3) > div > div > div > form > div.mktoButtonRow {
            border-top: ${rem(24)} solid ${colorMode === 'dark' ? colors.gray[800] : colors.white};
            z-index: 1;
            position: sticky;
            bottom: 0;
          }

          div#__next > div:nth-child(3) > div > div > div > form > div.mktoButtonRow::before {
            content: "";
            display: block;
            width: 20px;
            height: 20px;
            position: absolute;
            top: 0;
            background-image: radial-gradient(circle at 100% 100%, transparent ${rem(25)}, ${
          colorMode === 'dark' ? colors.gray[800] : colors.white
        } ${rem(8)});
          }

          div#__next > div:nth-child(3) > div > div > div > form > div.mktoButtonRow > span > button {
            margin: 0 !important;
          }

          div#__next > div:nth-child(3) {
            min-height: 100%;
            height: 100%;
          }

          div#__next > div:nth-child(3) > div {
            min-height: 100%;
            height: 100%;
          }

          html, body, div#__next {
            min-height: 100%;
            height: 100%;
          }
        }
      `;

        iframeDoc?.head.appendChild(style);
      }

      formContainer.style.minWidth = '100%';
    };

    tryFindForm();
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === CLOSE_EVENT_DATA) {
        onClose();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [onClose]);

  const trackingParam = {
    ...defaultBpEventParams,
    ...tracking.params,
    is_new_pricing: userData ? isSeatTierPlan(userData?.membership?.type) : null,
    device_type: getDeviceType(),
    action_type: 'click',
  };

  const mkcCode = modalConfig?.mkcCode ? '&mkc=' + modalConfig?.mkcCode : '';
  const iframeSrc =
    ADVANCED_ENTERPRISE_UPSELL_FORM_URL +
    mkcCode +
    '&tracking_param=' +
    encodeURIComponent(JSON.stringify(trackingParam));

  const customFooterLinkText = getEnterpriseFooterLinkText(modalConfig?.customFooterLinkText);

  const handleClick = () => {
    BpTracking.trackButtonClick(
      {
        ...trackingProps.params,
        copy: customFooterLinkText,
        device_type: trackingProps.device_type,
        path: window.location.href,
        page: window.parent.location.pathname,
        location: 'upsell_modal',
        target: '/enterprise',
        target_path: '/upgrade',
        feature: 'upgrade_cta',
      },
      trackingProps.bpEventVersion,
    );

    if (trackingProps.onButtonClick) {
      trackingProps.onButtonClick({
        copy: customFooterLinkText,
        target_path: '/enterprise',
      });
    }
  };

  return (
    <Modal
      data-testid="enterprise-upsell-container"
      isOpen={true}
      onClose={onClose}
      scrollBehavior="outside"
    >
      <ModalOverlay zIndex={1200} />
      {isLoadingIframe && (
        <Flex
          position={'absolute'}
          top={0}
          left={0}
          justifyContent={'center'}
          alignItems={'center'}
          width={'100%'}
          height={'100%'}
          visibility={'visible'}
          zIndex={1600}
        >
          <Spinner size="md" color={colors.vimeoBlue['600']} />
        </Flex>
      )}
      <ModalContent
        maxW={isMobile ? rem(375) : rem(940)}
        maxH={isMobile ? '100vh' : rem(820)}
        padding={0}
        visibility={isLoadingIframe ? 'hidden' : 'visible'}
        zIndex={1700}
      >
        <DismissIcon onClose={onClose} />
        <ModalBody
          padding={0}
          maxH={isMobile ? '100vh' : rem(820)}
          overflow={'hidden'}
          borderRadius={rem(20)}
        >
          <Grid
            templateColumns={`${
              !isMobile
                ? `minmax(${rem(268)}, ${rem(340)}) minmax(${rem(350)}, ${rem(600)})`
                : '1fr'
            }`}
            height={'100%'}
            maxH={isMobile ? '100vh' : rem(820)}
            sx={{
              '::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <Flex
              flexDirection={'column'}
              justifyContent={'space-between'}
              backgroundColor={colorMode === 'dark' ? colors.gray['900'] : colors.blackAlpha['900']}
              height={isMobile ? rem(172) : '100%'}
              maxWidth={isMobile ? '100%' : rem(400)}
              minWidth="md"
              padding={isMobile ? 6 : `${rem(32)} ${rem(40)}`}
              borderRadius={isMobile ? `${rem(20)} ${rem(20)} 0 0` : `${rem(20)} 0 0 ${rem(20)}`}
            >
              <Flex flex={'1'} flexDirection={'column'} color={colors.white} paddingRight={6}>
                <Box>
                  <Title modalConfig={modalConfig} />
                  {!isMobile && (
                    <Flex
                      direction={'column'}
                      fontSize={{ xs: 'body-md', sm: 'body-md', lg: 'body-lg' }}
                    >
                      {modalConfig?.customFeaturesList || <FeatureItems />}
                    </Flex>
                  )}
                </Box>
              </Flex>
              {!isMobile && (
                <Link
                  href="/enterprise"
                  variant="primary"
                  textDecoration={'underline'}
                  color={colors.white}
                  onClick={handleClick}
                >
                  {customFooterLinkText}
                  <ArrowRightSmall marginLeft={rem(6)} />
                </Link>
              )}
            </Flex>
            <Iframe title={translations.contactUs} src={iframeSrc} onLoad={iFrameLoadHandler} />
          </Grid>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EnterpriseModal;
